import * as Sentry from "@sentry/react";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { faFloppyDisk, faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
	ButtonStandard,
	FormSubscribe,
	ModalConfirmation,
	PYForm,
	Breadcrumbs as PyBreadcrumbs,
	Spinner,
	SubmitButton,
	toast,
} from "@performyard/py-components";

import Breadcrumbs from "../../../components/Breadcrumbs";
import Card from "../../../components/Card";
import Spacer from "../../../components/Spacer";
import TemplateTitleInput from "./TemplateTitleInput";
import TodoListEditor from "./TodoListEditor";

import { useRbacPrimaryRolePermissions } from "../../../api/hooks";
import { useIsNewNavigation } from "../../../api/hooks/defs/navigation";
import {
	useCreateWorkflowTemplate,
	useDeleteWorkflowTemplate,
	useUpdateWorkflowTemplate,
	useWorkflowTemplate,
} from "../../../api/hooks/defs/workflow-templates";
import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";

import { describeWorkflowTemplateError } from "../../../api/services/defs/workflow-templates";

import { getCompanySettingsContext, settingsHaveBeenFetched } from "../../../selectors/settings";

import BasePageTemplate from "../../../pageTemplates/BasePageTemplate";
import { NAME_MAX_LENGTH, TemplateFormSchema, TemplateFormValues, toSavePayload, toTodoRows } from "./todoForm";

const WORKFLOWS_ADMIN_URL = "/administration/workflows/";
const FALLBACK_NAME = "New Workflow Template";
const NAME_TRUNCATION_LENGTH = 30;

const snapshotOf = (name: string, todos: TemplateFormValues["todos"] | undefined): string =>
	JSON.stringify(toSavePayload(name, todos ?? []));

const InfoBox = ({ message, children }: { message: string; children?: React.ReactNode }) => (
	<div className="flex w-[400px] flex-col gap-2 rounded-sm bg-primary-1 p-5">
		<div className="flex gap-1.5">
			<FontAwesomeIcon icon={faInfoCircle} className="text-lg text-primary-7" />
			<div className="font-semibold">{message}</div>
		</div>
		{children}
	</div>
);

/**
 * Create/edit page for workflow templates (`/edit-workflow/:workflowTemplateID?`). In
 * create mode the template name arrives via the `?name=` param from the Workflows Admin
 * page's naming modal, and nothing is persisted until "Save Template" — the server
 * requires at least one to-do, so an empty template can't exist.
 */
const WorkflowTemplateEditor = () => {
	const { workflowTemplateID } = useParams<{ workflowTemplateID: string }>();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const location = useLocation();
	const isNewNav = useIsNewNavigation();
	const isEditing = !!workflowTemplateID;

	// Feature + permission gate, matching the Workflows Admin listing page.
	const {
		value: workflowsFeatureVisible,
		query: { isLoading: isLoadingWorkflowsFF },
	} = useFeatureFlagQuery("ff-is-workflows-enabled", true);
	const { value: docSigningFeatureVisible } = useFeatureFlagQuery("ff-is-document-signing-enabled", true);
	const { data: rbacPermissions, isLoading: isRbacLoading } = useRbacPrimaryRolePermissions("workflows");
	const companySettings = useSelector(getCompanySettingsContext);
	const haveSettingsBeenFetched = useSelector(settingsHaveBeenFetched);

	const workflowsEnabled = companySettings.getIn(["workflows", "state"], "disabled") === "enabled";
	const docSigningEnabled =
		companySettings.getIn(["documentSigning", "state"], "disabled") === "enabled" && docSigningFeatureVisible;

	const isGateLoading = isLoadingWorkflowsFF || isRbacLoading || !haveSettingsBeenFetched;
	const featureAvailable = workflowsFeatureVisible && workflowsEnabled;
	// The template read/write endpoints gate on workflows:manageAdmin (no overlord branch),
	// so the page requires manageAdmin rather than viewAdmin — a viewAdmin-only user
	// (billing-admin, overlord) would see an editor whose reads and writes all 403.
	const canManage = rbacPermissions?.includes("workflows:manageAdmin") ?? false;

	const [isDeleting, setIsDeleting] = useState(false);

	const {
		data: templateData,
		error: templateError,
		isLoading: isTemplateLoading,
		isError: isTemplateError,
		refetch: refetchTemplate,
		isFetching: isTemplateFetching,
	} = useWorkflowTemplate(workflowTemplateID ?? "", {
		enabled: isEditing && !isGateLoading && featureAvailable && canManage && !isDeleting,
	});

	const templateStatus = (templateError as { response?: { status?: number } } | null)?.response?.status;
	const isTemplateMissing = isTemplateError && templateStatus === 404;

	// The template name lives outside the form: it's set by the naming modal (create) or
	// the pencil-icon title editor, not by a form field.
	const initialCreateName = (searchParams.get("name") ?? "").trim().slice(0, NAME_MAX_LENGTH);
	const [name, setName] = useState<string | null>(isEditing ? null : initialCreateName || FALLBACK_NAME);
	if (isEditing && name === null && templateData) {
		setName(templateData.template.name);
	}

	const initialTodos = useMemo(
		() =>
			isEditing && templateData
				? toTodoRows(
						templateData.template.todos,
						templateData.display_user_map,
						templateData.signature_request_map
					)
				: [],
		// The form is only mounted once per template load; re-deriving on refetch would
		// discard in-progress edits.

		[isEditing, !!templateData]
	);

	const [savedSnapshot, setSavedSnapshot] = useState<string | null>(() =>
		isEditing ? null : snapshotOf(initialCreateName || FALLBACK_NAME, [])
	);
	if (isEditing && savedSnapshot === null && templateData) {
		setSavedSnapshot(snapshotOf(templateData.template.name, initialTodos));
	}

	const createMutation = useCreateWorkflowTemplate();
	const updateMutation = useUpdateWorkflowTemplate();
	const deleteMutation = useDeleteWorkflowTemplate();
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

	const reportWriteFailure = (error: unknown, mode: "create" | "update" | "delete"): void => {
		const isValidationError = !!(error as { data?: { errors?: unknown } } | null)?.data?.errors;
		if (isValidationError) return;
		Sentry.captureException(error, {
			tags: { surface: "workflow-template-editor" },
			extra: { mode, workflowTemplateID },
		});
	};

	const handleSubmit = async ({ value }: { value: TemplateFormValues }): Promise<void> => {
		const payload = toSavePayload(name ?? FALLBACK_NAME, value.todos);
		try {
			if (isEditing) {
				await updateMutation.mutateAsync({ workflowTemplateId: workflowTemplateID, payload });
			} else {
				const response = await createMutation.mutateAsync(payload);
				navigate(`/edit-workflow/${response.template.id}/`, { replace: true });
			}
			setSavedSnapshot(JSON.stringify(payload));
			toast.success("Template has been saved");
		} catch (error) {
			reportWriteFailure(error, isEditing ? "update" : "create");
			toast.error(describeWorkflowTemplateError(error, "There was an error saving the workflow template."));
		}
	};

	const handleDelete = async (): Promise<void> => {
		if (!workflowTemplateID) {
			setIsDeleteModalOpen(false);
			navigate(WORKFLOWS_ADMIN_URL);
			return;
		}
		try {
			setIsDeleting(true);
			await deleteMutation.mutateAsync(workflowTemplateID);
			setIsDeleteModalOpen(false);
			toast.success("Workflow template deleted.");
			navigate(WORKFLOWS_ADMIN_URL);
		} catch (error) {
			setIsDeleting(false);
			reportWriteFailure(error, "delete");
			toast.error(describeWorkflowTemplateError(error, "There was an error deleting the workflow template."));
		}
	};

	const renderGatedContent = (): React.ReactNode => {
		const spinner = (
			<div className="flex justify-center py-20" data-testid="template-editor-loading">
				<Spinner size={32} />
			</div>
		);

		if (isGateLoading) {
			return spinner;
		}
		if (!featureAvailable) {
			return <InfoBox message="This feature is not enabled for your organization." />;
		}
		if (!canManage) {
			return <InfoBox message="Missing required permissions." />;
		}
		if (isEditing && isTemplateMissing) {
			return <InfoBox message="This workflow template could not be found." />;
		}
		if (isEditing && isTemplateError) {
			return (
				<InfoBox message="This workflow template could not be loaded.">
					<ButtonStandard
						type="button"
						variant="outline"
						color="neutral"
						data-testid="retry-template-load"
						disabled={isTemplateFetching}
						onClick={() => void refetchTemplate()}
					>
						Try Again
					</ButtonStandard>
				</InfoBox>
			);
		}
		if (isEditing && (isTemplateLoading || !templateData)) {
			return spinner;
		}

		return (
			<PYForm
				id="workflow-template-form"
				schema={TemplateFormSchema}
				defaultValues={{ todos: initialTodos }}
				onSubmit={handleSubmit}
			>
				<div className="flex gap-[16px]">
					<Card className="min-w-0 flex-1" padding="40px">
						<TemplateTitleInput value={name ?? FALLBACK_NAME} onChange={setName} />
						<Spacer height={6} />
						<TodoListEditor docSigningEnabled={docSigningEnabled} />
					</Card>

					<div className="max-w-[208px] flex-shrink-0">
						<Card className="sticky top-2 flex h-fit w-[176px] flex-col gap-2" padding="16px">
							<FormSubscribe id="workflow-template-save" selector={(state) => state.values.todos}>
								{(todos: TemplateFormValues["todos"]) => (
									<SubmitButton
										color="green"
										iconLeft={faFloppyDisk}
										disabled={
											(todos?.length ?? 0) === 0 ||
											snapshotOf(name ?? FALLBACK_NAME, todos) === savedSnapshot
										}
									>
										Save Template
									</SubmitButton>
								)}
							</FormSubscribe>
							<ButtonStandard
								type="button"
								variant="outline"
								color="neutral"
								iconLeft={faTrashCan}
								data-testid="delete-template-button"
								onClick={() => setIsDeleteModalOpen(true)}
							>
								Delete Template
							</ButtonStandard>
						</Card>
					</div>
				</div>
			</PYForm>
		);
	};

	const displayName = name ?? "";
	const truncatedName =
		displayName.length > NAME_TRUNCATION_LENGTH
			? `${displayName.slice(0, NAME_TRUNCATION_LENGTH)}...`
			: displayName;

	return (
		<BasePageTemplate
			title={isEditing ? "Edit Workflow Template" : "Create Workflow Template"}
			backgroundColor="grey"
		>
			<div className="m-auto mt-[24px] w-[1104px] max-w-[calc(100vw-32px)]">
				{isNewNav ? (
					<PyBreadcrumbs
						items={[
							{ pageId: "workflows-admin", name: "Workflows", href: WORKFLOWS_ADMIN_URL },
							{ pageId: "workflow-template", name: truncatedName, href: location.pathname },
						]}
					/>
				) : (
					<Breadcrumbs
						pages={[
							{ pageID: "workflows-admin", label: "Workflows", url: WORKFLOWS_ADMIN_URL },
							{ pageID: "workflow-template", label: truncatedName, url: location.pathname },
						]}
					/>
				)}
				<Spacer height={5} />
				{renderGatedContent()}
			</div>

			<ModalConfirmation
				isOpen={isDeleteModalOpen}
				onClose={() => setIsDeleteModalOpen(false)}
				dangerouslySetClassName="w-[432px] max-w-[calc(100vw-48px)]"
				title="Delete Template?"
				subtitle={
					<span className="text-paragraph-2">
						{`Are you sure you want to delete ${truncatedName} workflow template? This action cannot be undone.`}
					</span>
				}
				negativeButtonProps={{
					children: "Cancel",
					variant: "outline",
					color: "neutral",
					onClick: () => setIsDeleteModalOpen(false),
				}}
				positiveButtonProps={{
					children: "Yes, Delete Template",
					color: "red",
					onClick: handleDelete,
					disabled: deleteMutation.isPending,
				}}
			/>
		</BasePageTemplate>
	);
};

export default WorkflowTemplateEditor;
